import { X, Mail, Phone, Building, Calendar, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui';
import { formatCurrency } from '@/utils';
import type { Lead, Deal } from '@/types';

export interface AnalyticsDetailData {
  title: string;
  subtitle?: string;
  leads?: Lead[];
  deals?: Deal[];
}

interface AnalyticsDetailModalProps {
  data: AnalyticsDetailData | null;
  onClose: () => void;
}

export function AnalyticsDetailModal({ data, onClose }: AnalyticsDetailModalProps) {
  if (!data) return null;

  const hasLeads = data.leads && data.leads.length > 0;
  const hasDeals = data.deals && data.deals.length > 0;
  const totalItems = (data.leads?.length ?? 0) + (data.deals?.length ?? 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[80vh] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
          <div>
            <h2 className="text-lg font-semibold text-white">{data.title}</h2>
            {data.subtitle && (
              <p className="text-sm text-zinc-400">{data.subtitle}</p>
            )}
            <p className="text-xs text-zinc-500 mt-1">
              {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-6">
          {hasLeads && (
            <div className={hasDeals ? 'mb-6' : ''}>
              {hasDeals && (
                <h3 className="text-sm font-medium text-zinc-300 mb-3">
                  Leads ({data.leads?.length})
                </h3>
              )}
              <div className="space-y-2">
                {data.leads?.map((lead) => (
                  <LeadRow key={lead.id} lead={lead} />
                ))}
              </div>
            </div>
          )}

          {hasDeals && (
            <div>
              {hasLeads && (
                <h3 className="text-sm font-medium text-zinc-300 mb-3">
                  Deals ({data.deals?.length})
                </h3>
              )}
              <div className="space-y-2">
                {data.deals?.map((deal) => (
                  <DealRow key={deal.id} deal={deal} />
                ))}
              </div>
            </div>
          )}

          {!hasLeads && !hasDeals && (
            <p className="text-center text-zinc-500 py-8">No data to display</p>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadRow({ lead }: { lead: Lead }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-zinc-800/50 p-4 hover:bg-zinc-800 transition-colors">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/20 text-sm font-bold text-blue-400">
          {lead.company.charAt(0)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-white truncate">{lead.company}</p>
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            {lead.contactName && (
              <span className="truncate">{lead.contactName}</span>
            )}
            {lead.contactTitle && (
              <span className="truncate">• {lead.contactTitle}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {lead.contactEmail && (
          <a
            href={`mailto:${lead.contactEmail}`}
            className="text-zinc-400 hover:text-blue-400 transition-colors"
            title={lead.contactEmail}
          >
            <Mail className="h-4 w-4" />
          </a>
        )}
        {lead.contactPhone && (
          <a
            href={`tel:${lead.contactPhone}`}
            className="text-zinc-400 hover:text-blue-400 transition-colors"
            title={lead.contactPhone}
          >
            <Phone className="h-4 w-4" />
          </a>
        )}
        <span className="text-xs px-2 py-1 rounded bg-zinc-700 text-zinc-300">
          {lead.stage}
        </span>
        <span className="text-xs text-zinc-500 min-w-[80px] text-right">
          {lead.createdAt}
        </span>
      </div>
    </div>
  );
}

function DealRow({ deal }: { deal: Deal }) {
  const stageColors: Record<string, string> = {
    'New': 'bg-blue-500/20 text-blue-400',
    'Discovery': 'bg-cyan-500/20 text-cyan-400',
    'Proposal': 'bg-orange-500/20 text-orange-400',
    'Negotiation': 'bg-yellow-500/20 text-yellow-400',
    'Closed Won': 'bg-emerald-500/20 text-emerald-400',
    'Closed Lost': 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="flex items-center justify-between rounded-lg bg-zinc-800/50 p-4 hover:bg-zinc-800 transition-colors">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/20 text-sm font-bold text-emerald-400">
          <DollarSign className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-white truncate">{deal.name}</p>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Building className="h-3 w-3" />
            <span className="truncate">{deal.company}</span>
            {deal.owner && <span>• {deal.owner}</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        <span className={`text-xs px-2 py-1 rounded ${stageColors[deal.stage] ?? 'bg-zinc-700 text-zinc-300'}`}>
          {deal.stage}
        </span>
        {deal.probability > 0 && (
          <span className="text-xs text-zinc-400">
            {deal.probability}%
          </span>
        )}
        {deal.value > 0 && (
          <span className="text-sm font-semibold text-emerald-400 min-w-[80px] text-right">
            {formatCurrency(deal.value)}
          </span>
        )}
        {deal.closeDate && (
          <div className="flex items-center gap-1 text-xs text-zinc-500 min-w-[100px]">
            <Calendar className="h-3 w-3" />
            {deal.closeDate}
          </div>
        )}
      </div>
    </div>
  );
}
